import { useResponsive } from "@/shared/hooks/useBreakpoints";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  DeleteOutlined,
  DownloadOutlined,
  EyeOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import {
  useDeleteDataFileMutation,
  useLazyGetDataFilesQuery,
} from "@/redux/service";
import { DateTime } from "luxon";
import { ColumnType } from "antd/es/table";

import DataFilesFilters from "./DataFilesFilters";
import CancelConfirmationModal from "@/components_v2/Modal/CancelConfirmationModal";
import BaseButton from "@/components_v2/Button/BaseButton";
import QueryTable from "@/components_v2/Tables/QueryTable";
import UserAvatar from "@/domains/users/components/UserAvatar";
import BaseTooltip from "@/components_v2/Tooltip/BaseTooltip";
import DataFileReferences from "@/features/files/list/components/DataFileReferences";
import { DataFileResponse } from "@/redux/files/types";
import {
  DataFilesFiltersState,
  DataFilesTableProps,
} from "@/features/files/list/types";
import { BaseUserResponse } from "@/redux/users/types";
import {
  BUTTON_TARGETS,
  BUTTON_TYPES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";

import "./styles.css";

const DataFilesTable = ({
  columnNames,
  filterNames,
  defaultFilters = {},
}: DataFilesTableProps) => {
  const { isMobile } = useResponsive();
  const { t } = useTranslation();

  const [deleteDataFile] = useDeleteDataFileMutation();
  const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState("");
  const [selectedFileId, setSelectedFileId] = useState<number | undefined>();
  const [filters, setFilters] = useState<DataFilesFiltersState>(
    defaultFilters as DataFilesFiltersState,
  );
  const [expandedRowKeys, setExpandedRowKeys] = useState<number[]>([]);

  const handleRemoveItem = (index: number) => {
    deleteDataFile(index);
    setOpenConfirmDeleteModal(false);
  };

  const handleDownloadFile = (file: DataFileResponse) => {
    const link = document.createElement("a");
    link.href = file.downloadUrl;
    link.download = file.name || "file";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleRowExpand = (fileId: number) => {
    if (expandedRowKeys.includes(fileId)) {
      setExpandedRowKeys(expandedRowKeys.filter((key) => key !== fileId));
    } else {
      setExpandedRowKeys([...expandedRowKeys, fileId]);
    }
  };

  const columns: Record<string, ColumnType<DataFileResponse>> = useMemo(
    () => ({
      name: {
        title: t("table.title"),
        fixed: !isMobile ? "left" : undefined,
        width: 450,
        ellipsis: true,
        dataIndex: "name",
        render: (value: string) => (
          <span className="data-files-table-title-container data-file-title body-s-regular">
            {value}
          </span>
        ),
      },
      extension: {
        title: t("table.fileType"),
        dataIndex: "extension",
        align: "center",
        render: (value: string) => (
          <p className="data-files-table-title-container body-s-regular">
            {value.replace(".", "").toUpperCase()}
          </p>
        ),
      },
      uploadedBy: {
        title: t("datablocks.labels.uploadedBy"),
        dataIndex: "createdBy",
        align: "center",
        render: (value: BaseUserResponse) => (
          <UserAvatar user={value} tooltip={value.fullName} />
        ),
      },
      uploadedAt: {
        title: t("table.dateUploaded"),
        dataIndex: "createdAt",
        align: "center",
        render: (value: string) => (
          <p className="data-files-table-title-container body-s-regular">
            {DateTime.fromISO(value).toFormat("dd MMMM yyyy")}
          </p>
        ),
      },
      references: {
        title: t("table.references"),
        dataIndex: "referencesCount",
        align: "center",
        render: (value: number, file: DataFileResponse) => (
          <p className="data-files-table-title-container body-s-regular">
            {t("datablocks.labels.referencesCount", {
              count: value,
              plural: value === 1 ? "" : "s",
            })}
            {file.referencesCount > 0 && (
              <div>
                <PlusOutlined
                  style={{
                    transform: expandedRowKeys.includes(file.id)
                      ? "rotate(45deg)"
                      : "rotate(0deg)",
                    transition:
                      "transform 0.3s cubic-bezier(0.645, 0.045, 0.355, 1)",
                    cursor: "pointer",
                  }}
                  onClick={() => handleRowExpand(file.id)}
                />
              </div>
            )}
          </p>
        ),
      },
      actions: {
        title: t("table.actions"),
        align: "center",
        render: (value: DataFileResponse) => (
          <div className="data-files-table-actions">
            <BaseTooltip title={t("datablocks.labels.viewFile")}>
              <BaseButton
                variant={BUTTON_VARIANTS.LINK}
                leadingIcon={<EyeOutlined />}
                target={BUTTON_TARGETS.BLANK}
                type={BUTTON_TYPES.LINK}
                to={value.pdfFile || value.file}
              />
            </BaseTooltip>
            <BaseTooltip title={t("datablocks.labels.downloadFile")}>
              <BaseButton
                variant={BUTTON_VARIANTS.LINK}
                leadingIcon={<DownloadOutlined />}
                onClick={() => handleDownloadFile(value)}
              />
            </BaseTooltip>
            <BaseTooltip
              title={
                value.referencesCount > 0
                  ? t("datablocks.labels.canNotDeleteFileWithReferences")
                  : t("datablocks.labels.deleteFile")
              }
            >
              <BaseButton
                disabled={value.referencesCount > 0}
                variant={BUTTON_VARIANTS.LINK}
                leadingIcon={<DeleteOutlined />}
                onClick={() => {
                  setOpenConfirmDeleteModal(true);
                  setSelectedFileName(value.name);
                  setSelectedFileId(value.id);
                }}
              />
            </BaseTooltip>
          </div>
        ),
      },
    }),
    [isMobile, t, expandedRowKeys],
  );

  const mappedColumns = useMemo(
    () =>
      columnNames.map(
        (columnKey) => columns[columnKey] as ColumnType<DataFileResponse>,
      ),
    [columns, columnNames],
  );

  return (
    <div className="data-blocks-table">
      <DataFilesFilters
        defaultFilters={defaultFilters}
        filterNames={filterNames}
        onFiltersChange={setFilters}
      />

      <QueryTable<DataFileResponse>
        query={useLazyGetDataFilesQuery}
        filters={filters}
        columns={mappedColumns}
        expandable={{
          showExpandColumn: false,
          expandedRowKeys: expandedRowKeys,
          rowExpandable: (file) => file.referencesCount > 0,
          expandedRowRender: (file) => <DataFileReferences fileId={file.id} />,
        }}
      />
      <CancelConfirmationModal
        open={openConfirmDeleteModal}
        onCancel={() => setOpenConfirmDeleteModal(false)}
        onConfirm={() =>
          selectedFileId !== undefined && handleRemoveItem(selectedFileId)
        }
        title={t("confirm.confirmDelete")}
        confirmButtonText={t("buttons.delete")}
        content={t("datablocks.labels.confirmDeleteModalContent", {
          name: selectedFileName,
        })}
      />
    </div>
  );
};

export default DataFilesTable;
